import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type { KratosFlow } from "@/lib/kratos";
import { firstFlowError, nodeMessages } from "@/lib/kratos";
import { useKratosFlow } from "@/features/auth/kratos/useKratosFlow";

/**
 * Kratos email-verification flow (code strategy).
 *
 * Two states drive the UI: `choose_method` asks for the email to send
 * a code to; `sent_email` asks for the code (with a resend control).
 * `passed_challenge` fires `onVerified` — the caller decides what
 * follows (exchange + continue, or a simple success note).
 */
export function KratosVerificationForm({
  flowId,
  initialFlow,
  initialCode,
  email,
  onVerified,
  testIdPrefix = "verify-kratos",
}: {
  flowId?: string;
  initialFlow?: KratosFlow;
  /** Code from an email deep-link (`?code=`) — auto-submitted once. */
  initialCode?: string;
  /** Prefill for the send-a-code form. */
  email?: string;
  onVerified: () => void | Promise<void>;
  testIdPrefix?: string;
}) {
  const [codeInput, setCodeInput] = useState("");
  const [emailInput, setEmailInput] = useState(email ?? "");
  const verifiedRef = useRef(false);

  const { flow, loading, submitting, error, submit } = useKratosFlow("verification", {
    flowId,
    initialFlow,
  });

  const state = flow?.state ?? "";

  // Fire onVerified exactly once when the flow passes the challenge.
  useEffect(() => {
    if (state === "passed_challenge" && !verifiedRef.current) {
      verifiedRef.current = true;
      void onVerified();
    }
  }, [state, onVerified]);

  // Email deep-link: auto-submit the code once the flow is ready.
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (!flow || !initialCode || autoSubmittedRef.current) return;
    if (flow.state !== "sent_email") return;
    autoSubmittedRef.current = true;
    void submit({ method: "code", code: initialCode });
  }, [flow, initialCode, submit]);

  if (loading || !flow) {
    return (
      <p data-testid={`${testIdPrefix}-loading`} className="font-mono text-xs text-ink-muted">
        Preparing verification...
      </p>
    );
  }

  if (state === "passed_challenge") {
    return (
      <p data-testid={`${testIdPrefix}-success`} className="font-mono text-xs text-good">
        Email verified.
      </p>
    );
  }

  const flowError = firstFlowError(flow);
  const codeMessages = nodeMessages(flow, "code");

  if (state === "sent_email") {
    return (
      <form
        data-testid={`${testIdPrefix}-code-form`}
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!codeInput.trim() || submitting) return;
          void submit({ method: "code", code: codeInput.trim() });
        }}
      >
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Verification code
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value)}
            autoFocus
            required
            data-testid={`${testIdPrefix}-code`}
            className="rounded border border-rule bg-canvas px-3 py-2.5 font-mono text-sm tracking-[0.3em] text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
          {codeMessages.map((message) => (
            <span key={message} className="font-mono text-[0.65rem] text-destructive">
              {message}
            </span>
          ))}
        </label>
        {flowError ? (
          <p data-testid={`${testIdPrefix}-error`} className="font-mono text-xs text-destructive">
            {flowError}
          </p>
        ) : null}
        {error ? (
          <p className="font-mono text-xs text-destructive">{error}</p>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => {
              if (submitting) return;
              // Re-submitting the email on a sent_email flow re-sends
              // the code.
              void submit({ method: "code", email: emailInput.trim() || undefined });
            }}
            data-testid={`${testIdPrefix}-resend`}
            className="rounded px-1 py-0.5 font-mono text-[0.65rem] text-ink-muted hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Resend code
          </button>
          <Button
            type="submit"
            disabled={submitting}
            data-testid={`${testIdPrefix}-submit`}
          >
            {submitting ? (
              "Verifying..."
            ) : (
              <>
                Verify email
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </form>
    );
  }

  // choose_method — ask for the address to send a code to.
  return (
    <form
      data-testid={`${testIdPrefix}-email-form`}
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!emailInput.trim() || submitting) return;
        void submit({ method: "code", email: emailInput.trim() });
      }}
    >
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Email
        </span>
        <input
          type="email"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          autoComplete="email"
          autoFocus
          required
          data-testid={`${testIdPrefix}-email`}
          className="rounded border border-rule bg-canvas px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      {flowError ? (
        <p data-testid={`${testIdPrefix}-error`} className="font-mono text-xs text-destructive">
          {flowError}
        </p>
      ) : null}
      {error ? <p className="font-mono text-xs text-destructive">{error}</p> : null}
      <Button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-send`}
        className="self-end"
      >
        {submitting ? "Sending..." : "Send verification code"}
      </Button>
    </form>
  );
}
